import React, {useEffect, useMemo, useState} from "react";
import {Button, Descriptions, Form, Input, Modal, Select, Space, Tag} from "antd";
import {message} from "../../utils/antdFeedback";
import {UserPlus} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {GenericTable} from "../../components/table/GenericTable";
import {AppDispatch, RootState} from "../../redux/store";
import {createAdminUser, getAdminPermissions, getAdminUsers, updateAdminUserPermissions} from "../../redux/slice/adminUserSlice";
import {HeroHeader, Card, COLORS} from "../../designSystem";

function AdminUsers() {
  const dispatch: AppDispatch = useDispatch();
  const {token} = useSelector((state: RootState) => state.account);
  const {users, permissions, loading} = useSelector((state: RootState) => state.adminUsers);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [viewing, setViewing] = useState<any>(null);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    if (token) {
      dispatch(getAdminUsers({token, searchParam: searchText, status: statusFilter}));
      dispatch(getAdminPermissions(token));
    }
  }, [token, dispatch, searchText, statusFilter]);

  const save = async (values: any) => {
    if (!token) return;
    try {
      if (editing) {
        await dispatch(updateAdminUserPermissions({token, userId: editing.id, permissions: values.permissions || []})).unwrap();
      } else {
        await dispatch(createAdminUser({...values, token, permissions: values.permissions || []})).unwrap();
      }
      message.success("Admin user saved");
      dispatch(getAdminUsers({token, searchParam: searchText, status: statusFilter}));
      setOpen(false);
      setEditing(null);
      form.resetFields();
    } catch (error: any) {
      message.error(error?.message || error || "Could not save admin user");
    }
  };

  const startEdit = (user: any) => {
    setEditing(user);
    form.setFieldsValue({...user, permissions: user.permissions || []});
    setOpen(true);
  };

  const filteredUsers = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    if (!query) return users;

    return users.filter((user: any) => {
      const searchable = [
        user.firstName,
        user.lastName,
        user.email,
        ...(user.roles || []),
        ...(user.permissions || []),
        user.isActive ? "active" : "inactive",
        user.isSuperAdmin ? "super admin" : "admin",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [searchText, users]);

  const permissionLabel = (code: string) => {
    const match = permissions.find((permission: any) => permission.code === code);
    return match ? match.description : code;
  };

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <HeroHeader
          icon={UserPlus}
          title="Admin Users"
          subtitle="Manage admin accounts and their permissions."
          actions={
            <button
              onClick={() => { setEditing(null); form.resetFields(); setOpen(true); }}
              className="inline-flex items-center gap-2 font-semibold h-12 px-6 rounded-xl border-0 cursor-pointer"
              style={{background: COLORS.white, color: COLORS.brand}}
            >
              <UserPlus className="w-4 h-4"/> New Admin
            </button>
          }
        />

        <Card className="mt-8 p-5">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <Input.Search
            allowClear
            placeholder="Search admin users by email, name, role, permission, or status"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            onSearch={setSearchText}
            style={{maxWidth: 520}}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{minWidth: 180}}
            options={[
              {label: "All admin users", value: ""},
              {label: "Active", value: "active"},
              {label: "Inactive", value: "inactive"},
              {label: "Admin", value: "admin"},
              {label: "Super admin", value: "superadmin"},
            ]}
          />
          <span className="text-sm text-gray-500">
            Showing {filteredUsers.length} of {users.length} admin users
          </span>
        </div>
        <GenericTable
          loading={loading}
          data={filteredUsers}
          pageInfo={{pageNumber: 1, pageSize: filteredUsers.length || 10, totalItems: filteredUsers.length, totalPages: 1}}
          columns={[
            {title: "Name", key: "name", render: (_: any, row: any) => `${row.firstName || ""} ${row.lastName || ""}`.trim() || "No name"},
            {title: "Email", dataIndex: "email", key: "email"},
            {title: "Roles", key: "roles", render: (_: any, row: any) => (row.roles || []).map((role: string) => <Tag key={role}>{role}</Tag>)},
            {title: "Permissions", key: "permissions", render: (_: any, row: any) => row.isSuperAdmin ? <Tag color="gold">*</Tag> : `${(row.permissions || []).length} permissions`},
            {title: "Status", key: "status", render: (_: any, row: any) => <Tag color={row.isActive ? "green" : "red"}>{row.isActive ? "Active" : "Inactive"}</Tag>},
            {
              title: "Action",
              key: "action",
              render: (_: any, row: any) => (
                <Space>
                  <Button onClick={() => setViewing(row)}>View</Button>
                  <Button disabled={row.isSuperAdmin} onClick={() => startEdit(row)}>Permissions</Button>
                </Space>
              ),
            },
          ]}
        />
        </Card>
        <Modal
          title="Admin User Details"
          open={!!viewing}
          onCancel={() => setViewing(null)}
          footer={[
            <Button key="close" onClick={() => setViewing(null)}>Close</Button>,
            <Button
              key="permissions"
              type="primary"
              disabled={viewing?.isSuperAdmin}
              onClick={() => {
                const user = viewing;
                setViewing(null);
                startEdit(user);
              }}
            >
              Manage Permissions
            </Button>,
          ]}
          width={780}
        >
          {viewing && (
            <div className="space-y-5">
              <Descriptions bordered column={1}>
                <Descriptions.Item label="Name">
                  {`${viewing.firstName || ""} ${viewing.lastName || ""}`.trim() || "No name"}
                </Descriptions.Item>
                <Descriptions.Item label="Email">{viewing.email}</Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={viewing.isActive ? "green" : "red"}>{viewing.isActive ? "Active" : "Inactive"}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Access Type">
                  {viewing.isSuperAdmin ? <Tag color="gold">Super Admin</Tag> : <Tag>Admin</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="User Id">{viewing.id}</Descriptions.Item>
              </Descriptions>

              <Descriptions title="Roles" bordered column={1}>
                <Descriptions.Item label="Assigned Roles">
                  {(viewing.roles || []).length
                    ? (viewing.roles || []).map((role: string) => <Tag key={role}>{role}</Tag>)
                    : "No roles assigned"}
                </Descriptions.Item>
              </Descriptions>

              <Descriptions title="Permissions" bordered column={1}>
                <Descriptions.Item label="Assigned Permissions">
                  {viewing.isSuperAdmin ? (
                    <Tag color="gold">All permissions</Tag>
                  ) : (viewing.permissions || []).length ? (
                    <div className="flex flex-wrap gap-2">
                      {(viewing.permissions || []).map((permission: string) => (
                        <Tag key={permission}>{permissionLabel(permission)}</Tag>
                      ))}
                    </div>
                  ) : (
                    "No permissions assigned"
                  )}
                </Descriptions.Item>
              </Descriptions>
            </div>
          )}
        </Modal>
        <Modal
          title={editing ? "Update Admin Permissions" : "Create Admin User"}
          open={open}
          onCancel={() => setOpen(false)}
          onOk={() => form.submit()}
          confirmLoading={loading}
          width={720}
        >
          <Form form={form} layout="vertical" onFinish={save}>
            {!editing && (
              <>
                <Form.Item label="Email" name="email" rules={[{required: true, type: "email"}]}><Input/></Form.Item>
                <Form.Item label="First Name" name="firstName"><Input/></Form.Item>
                <Form.Item label="Last Name" name="lastName"><Input/></Form.Item>
                <Form.Item label="Password" name="password" rules={[{required: true}]}><Input.Password/></Form.Item>
              </>
            )}
            <Form.Item label="Permissions" name="permissions">
              <Select mode="multiple" optionFilterProp="label">
                {permissions.map((permission: any) => (
                  <Select.Option key={permission.code} value={permission.code} label={`${permission.code} ${permission.description}`}>
                    {permission.description}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </main>
    </AdminLayout>
  );
}

export default AdminUsers;
